import { Clock, Utensils } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import { FormInput, TimePicker } from '../../../../components'
import { LUNCH_BREAK_MINUTES } from '../constants/hrmsAttendanceSettings.constants'
import { timeToMinutes } from '../schemas/attendanceSettings.schema'
import type { AttendanceSettingsFormValues } from '../types/hrmsAttendanceSettings.types'

interface WorkingHoursCardProps {
  form: UseFormReturn<AttendanceSettingsFormValues>
  isEditing: boolean
  onToggleEdit: () => void
  isReadOnly?: boolean
}

export function WorkingHoursCard({
  form,
  isEditing,
  onToggleEdit,
  isReadOnly = false,
}: WorkingHoursCardProps) {
  const {
    register,
    watch,
    setValue,
    formState: { errors },
  } = form

  const shiftMode = watch('shiftMode')
  const workStart = watch('workStart')
  const workEnd = watch('workEnd')
  const grace = watch('gracePeriodMinutes')
  const dailyWorkingHours = watch('dailyWorkingHours')

  const startMin = timeToMinutes(workStart)
  const endMin = timeToMinutes(workEnd)
  const grossMinutes = Math.max(0, endMin - startMin)
  const netMinutes = Math.max(0, grossMinutes - LUNCH_BREAK_MINUTES)
  const calculatedNetHours = (netMinutes / 60).toFixed(1)

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 min-w-0 font-sans">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 min-w-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-slate-700 shrink-0">
            <Clock className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900 truncate">Working Hours</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Define core institutional operational hours and working week.
            </p>
          </div>
        </div>

        {!isReadOnly && (
          <button
            type="button"
            onClick={onToggleEdit}
            className="text-xs font-semibold text-[#146971] hover:underline whitespace-nowrap shrink-0 pt-1 cursor-pointer"
          >
            {isEditing ? 'Done Editing' : 'Edit Settings'}
          </button>
        )}
      </div>

      {/* Body */}
      {isEditing && !isReadOnly ? (
        <div className="space-y-4 pt-1">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Shift Mode: read-only until shift-modes endpoint exists */}
            {/* TODO: Wire shift mode dropdown once a shift-modes endpoint is available */}
            <div className="flex flex-col gap-1.5">
              <FormInput
                label="Shift Mode"
                value={shiftMode || 'Fixed Academic Hours'}
                readOnly
                disabled
                className="bg-slate-50 cursor-not-allowed"
              />
              <span className="text-xs text-slate-400">Read-only (configured per institutional calendar)</span>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700">Work Start Time</label>
              <TimePicker
                value={workStart}
                onChange={(val) => setValue('workStart', val, { shouldValidate: true, shouldDirty: true })}
              />
              {errors.workStart?.message && (
                <p className="text-xs text-red-500 mt-1">{errors.workStart.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-slate-700">Work End Time</label>
              <TimePicker
                value={workEnd}
                onChange={(val) => setValue('workEnd', val, { shouldValidate: true, shouldDirty: true })}
              />
              {errors.workEnd?.message && (
                <p className="text-xs text-red-500 mt-1">{errors.workEnd.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <FormInput
                label="Daily Working Hours"
                type="number"
                step={0.5}
                min={1}
                max={24}
                {...register('dailyWorkingHours', { valueAsNumber: true })}
                error={errors.dailyWorkingHours?.message}
              />
            </div>
          </div>

          <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-200 text-xs">
            <div className="flex items-center gap-2 text-slate-600">
              <Utensils className="h-4 w-4 text-slate-400" />
              <span>Mandatory Lunch Break: <strong className="font-semibold text-slate-800">60 Minutes</strong> (Deducted automatically)</span>
            </div>
            <div className="text-slate-700 font-semibold">
              Calculated Net Working Time: <span className="text-[#146971] text-sm font-bold">{calculatedNetHours} hrs/day</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3 pt-1">
          {/* Row 1 */}
          <div className="mt-4 rounded-xl bg-gray-50 p-4 flex justify-between items-end border border-slate-200/60">
            <div>
              <span className="uppercase text-[11px] font-semibold tracking-wide text-gray-500 block">
                STANDARD WORK SCHEDULE
              </span>
              <span className="text-xl font-bold text-slate-900 mt-1 block">
                {workStart} – {workEnd}
              </span>
            </div>

            <div className="text-right">
              <span className="uppercase text-[11px] font-semibold tracking-wide text-gray-500 block">
                SHIFT MODE
              </span>
              <span className="font-semibold text-slate-900 text-sm mt-1 block">
                {shiftMode || 'Fixed Academic Hours'}
              </span>
            </div>
          </div>

          {/* Row 2 */}
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-gray-50 p-4 border border-slate-200/60">
              <span className="uppercase text-[11px] font-semibold tracking-wide text-gray-500 block">
                DAILY WORKING HOURS
              </span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-slate-900">{dailyWorkingHours ?? 8}</span>
                <span className="text-sm text-slate-500 font-normal">Hours / Day</span>
              </div>
            </div>

            <div className="rounded-xl bg-gray-50 p-4 border border-slate-200/60">
              <span className="uppercase text-[11px] font-semibold tracking-wide text-gray-500 block">
                ARRIVAL GRACE PERIOD
              </span>
              <div className="mt-1 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-slate-900">{grace ?? 15}</span>
                <span className="text-sm text-slate-500 font-normal">Minutes</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
